import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageWrapper from '../components/PageWrapper';
import { PAGE_CTAS } from '../components/marketing/pageCtas';
import MarketingHero from '../components/marketing/MarketingHero';
import MarketingSection from '../components/marketing/MarketingSection';
import FadeIn from '../components/marketing/FadeIn';
import ReplacementCostExplorer from '../components/about/ReplacementCostExplorer';
import { EASE_REFINED } from '../components/marketing/constants';

const approachPillars = [
  {
    number: '01',
    title: 'Predict early',
    body: 'Stavyn surfaces flight risk months before resignations. Our 47-factor model combines internal signals with market and compensation data so you see risk before it\'s obvious.',
  },
  {
    number: '02',
    title: 'Understand why',
    body: 'Risk scores alone aren\'t enough. We explain which factors drive each employee\'s risk — career growth, compensation, tenure, market demand — so you know where to focus.',
  },
  {
    number: '03',
    title: 'Act with clarity',
    body: 'Get tailored retention playbooks and next steps, not generic advice. Leaders can prioritize conversations and interventions with confidence.',
  },
];

const problemItems = [
  { n: '01', text: 'Companies usually learn someone is leaving only after they resign.' },
  { n: '02', text: 'HR teams lack real-time data on retention risk across their organization.' },
  { n: '03', text: 'Compensation, career growth, and market demand constantly shift — making static reviews obsolete.' },
];

export default function AboutPage() {
  return (
    <PageWrapper cta={PAGE_CTAS.about}>
      <MarketingHero
        eyebrow="About Stavyn"
        title="Predicting the future of"
        accent="employee retention."
        accentBlock
        subtitle="Stavyn was built to solve one of the most expensive blind spots in modern companies — losing great employees before leaders even realize they're at risk."
      />

      <MarketingSection padding="lg" maxWidth="6xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <FadeIn delay={0.1}>
            <div className="section-tag marketing-section-label">Our Mission</div>
            <h2 className="landing-heading marketing-section-heading">
              Retention intelligence for the companies that care most.
            </h2>
            <p className="marketing-prose__p" style={{ fontSize: '1rem', maxWidth: 520 }}>
              US companies spend over $1 trillion annually replacing employees who could have been retained with the right insight at the right time. Stavyn uses artificial intelligence to identify flight risk months in advance and provide leaders with clear, actionable steps to keep their best people.
            </p>
          </FadeIn>
          <FadeIn delay={0.25}>
            <ReplacementCostExplorer />
          </FadeIn>
        </div>
      </MarketingSection>

      <MarketingSection padding="lg" maxWidth="5xl">
        <FadeIn delay={0.1}>
          <div className="section-tag marketing-section-label">Why Stavyn Exists</div>
          <h2
            className="landing-heading"
            style={{
              fontSize: 'clamp(2rem, 3.5vw, 3rem)',
              lineHeight: 1.15,
              letterSpacing: '-0.035em',
              color: 'var(--text-primary)',
              marginBottom: '3.5rem',
              maxWidth: 600,
            }}
          >
            The problem has always been{' '}
            <span className="headline-accent hero-headline-accent">timing.</span>
          </h2>
        </FadeIn>

        <div className="about-problem-list">
          {problemItems.map((item, i) => (
            <FadeIn key={item.n} delay={0.1 + i * 0.12}>
              <div className="about-problem-list__item">
                <span className="about-problem-list__num">{item.n}</span>
                <p className="about-problem-list__text">{item.text}</p>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={0.1}>
          <div className="glass-panel about-solution-card">
            <div className="about-solution-card__label">The Solution</div>
            <p className="about-solution-card__text">
              Stavyn combines internal HR data, market intelligence, and AI analysis to identify risk signals before they become resignations — giving leaders the time and clarity to act.
            </p>
          </div>
        </FadeIn>
      </MarketingSection>

      <MarketingSection padding="lg" maxWidth="5xl">
        <FadeIn delay={0.1}>
          <div className="about-founder-block">
            <blockquote className="about-founder-block__quote">
              "We built Stavyn because leaders shouldn't have to guess who's about to leave — they deserve months of runway, not a resignation letter."
            </blockquote>
            <div>
              <p className="marketing-prose__p" style={{ fontSize: '1rem', marginBottom: '1.25rem' }}>
                Our founder vision is simple: retention should be as predictive as every other strategic function in modern business. Finance has forecasting. Sales has pipeline. HR deserves the same clarity.
              </p>
              <p className="marketing-prose__p" style={{ fontSize: '1rem', margin: 0 }}>
                Stavyn exists to give organizations that care deeply about their people the intelligence to protect their most valuable asset — before it's too late.
              </p>
            </div>
          </div>
        </FadeIn>
      </MarketingSection>

      <MarketingSection padding="lg" maxWidth="6xl">
        <FadeIn delay={0.1}>
          <div className="section-tag marketing-section-label">Our approach</div>
          <h2 className="landing-heading marketing-section-heading">
            Predict, understand, then{' '}
            <span className="headline-accent hero-headline-accent">act.</span>
          </h2>
        </FadeIn>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {approachPillars.map((pillar, i) => (
            <FadeIn key={pillar.number} delay={0.1 + i * 0.1}>
              <motion.article
                className="luxury-card glass-panel about-pillar-card"
                whileHover={{ y: -4 }}
                transition={{ duration: 0.35, ease: EASE_REFINED }}
              >
                <span className="about-pillar-card__num">{pillar.number}</span>
                <h3 className="about-pillar-card__title">{pillar.title}</h3>
                <p className="about-pillar-card__body">{pillar.body}</p>
              </motion.article>
            </FadeIn>
          ))}
        </div>
      </MarketingSection>

      <section className="marketing-section marketing-section--closing">
        <div className="max-w-3xl mx-auto px-8 lg:px-12 text-center">
          <FadeIn delay={0.1}>
            <h2
              className="landing-heading"
              style={{
                fontSize: 'clamp(2.25rem, 4vw, 3.5rem)',
                lineHeight: 1.1,
                letterSpacing: '-0.035em',
                color: 'var(--text-primary)',
                marginBottom: '1.5rem',
              }}
            >
              How Stavyn helps{' '}
              <span className="headline-accent hero-headline-accent">organizations.</span>
            </h2>
            <p className="marketing-hero__subtitle" style={{ maxWidth: 480, margin: '0 auto 2.5rem' }}>
              From early risk signals to tailored retention playbooks, Stavyn gives HR and people leaders the clarity to act before turnover becomes inevitable.
            </p>
            <Link to="/schedule-demo" className="landing-cta landing-cta--hero">
              Get Free Risk Report
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M2 6h8M7 3l3 3-3 3" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </FadeIn>
        </div>
      </section>
    </PageWrapper>
  );
}
